/**
 * Team Admin Index Page - List and manage team members
 * @file src/web/features/team/pages/admin/index.tsx
 */

import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardTitle, CardContent, CardDescription } from '@voilajsx/uikit/card';
import { Button } from '@voilajsx/uikit/button';
import { useTeam } from '../../hooks/useTeam';
import { SEO } from '../../../../shared/components';
import {
  Users,
  Plus,
  Edit,
  Trash2,
  Eye,
  EyeOff,
  Loader2,
  User
} from 'lucide-react';

const TeamAdminPage: React.FC = () => {
  const { members, loading, error, deleteMember, updateMember, refetch } = useTeam(true);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [togglingId, setTogglingId] = useState<number | null>(null);

  const handleDelete = async (id: number, name: string) => {
    if (!confirm(`Are you sure you want to delete "${name}"? This action cannot be undone.`)) {
      return;
    }

    setDeletingId(id);
    try {
      await deleteMember(id);
    } catch (err: any) {
      alert(err.message || 'Failed to delete team member');
    } finally {
      setDeletingId(null);
    }
  };

  const handleToggleActive = async (id: number, currentStatus: boolean) => {
    setTogglingId(id);
    try {
      await updateMember(id, { isActive: !currentStatus });
    } catch (err: any) {
      alert(err.message || 'Failed to update team member');
    } finally {
      setTogglingId(null);
    }
  };

  if (loading) {
    return (
      <div className="container mx-auto px-4 max-w-7xl py-8">
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-cyan-600" />
          <span className="ml-2 text-gray-600">Loading team members...</span>
        </div>
      </div>
    );
  }

  return (
    <>
      <SEO title="Manage Team - Admin Panel" />
      <div className="container mx-auto px-4 max-w-7xl py-8">
        <div className="space-y-6">
          {/* Header */}
          <div className="bg-gradient-to-r from-cyan-600 via-blue-600 to-indigo-600 rounded-lg p-6 shadow-lg">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-white/20 rounded-lg backdrop-blur-sm">
                  <Users className="h-8 w-8 text-white" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold tracking-tight text-white">
                    Team Management
                  </h1>
                  <p className="mt-1 text-cyan-100 text-sm">
                    Manage teacher profiles and staff information
                  </p>
                </div>
              </div>
              <Link to="/team/admin/create">
                <Button className="bg-white text-cyan-600 hover:bg-cyan-50">
                  <Plus className="mr-2 h-4 w-4" />
                  Add Member
                </Button>
              </Link>
            </div>
          </div>

          {/* Error Message */}
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
              {error}
              <button onClick={refetch} className="ml-2 underline">Retry</button>
            </div>
          )}

          {/* Team Members List */}
          <Card className="border-t-4 border-t-cyan-600 shadow-md overflow-hidden p-0">
            <div className="bg-gradient-to-r from-cyan-50 to-blue-50 px-6 py-5">
              <CardTitle className="flex items-center gap-2">
                <div className="p-2 bg-cyan-100 rounded-lg">
                  <User className="h-5 w-5 text-cyan-600" />
                </div>
                Team Members ({members.length})
              </CardTitle>
              <CardDescription className="mt-1.5">
                Click on a member to edit their profile
              </CardDescription>
            </div>

            <CardContent className="p-0">
              {members.length === 0 ? (
                <div className="p-8 text-center text-gray-500">
                  <Users className="mx-auto h-12 w-12 text-gray-300 mb-4" />
                  <p>No team members yet.</p>
                  <Link to="/team/admin/create" className="text-cyan-600 hover:underline mt-2 inline-block">
                    Add your first team member
                  </Link>
                </div>
              ) : (
                <div className="divide-y divide-gray-100">
                  {members.map((member) => (
                    <div
                      key={member.id}
                      className={`p-4 flex items-center gap-4 hover:bg-gray-50 transition-colors ${
                        !member.isActive ? 'opacity-60 bg-gray-50' : ''
                      }`}
                    >
                      {/* Profile Image */}
                      <div className="w-16 h-16 rounded-full overflow-hidden bg-gray-200 flex-shrink-0">
                        {member.imageUrl ? (
                          <img
                            src={member.imageUrl}
                            alt={member.name}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center bg-cyan-100">
                            <User className="w-8 h-8 text-cyan-600" />
                          </div>
                        )}
                      </div>

                      {/* Member Info */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <h3 className="font-semibold text-gray-900 truncate">{member.name}</h3>
                          {!member.isActive && (
                            <span className="text-xs bg-gray-200 text-gray-600 px-2 py-0.5 rounded">
                              Hidden
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-cyan-600 font-medium">{member.position}</p>
                        {member.qualification && (
                          <p className="text-sm text-gray-500">{member.qualification}</p>
                        )}
                      </div>

                      {/* Order */}
                      <div className="text-sm text-gray-400">
                        Order: {member.order}
                      </div>

                      {/* Actions */}
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleToggleActive(member.id, member.isActive)}
                          disabled={togglingId === member.id}
                          className={`p-2 rounded-lg transition-colors ${
                            member.isActive
                              ? 'text-green-600 hover:bg-green-50'
                              : 'text-gray-400 hover:bg-gray-100'
                          }`}
                          title={member.isActive ? 'Hide member' : 'Show member'}
                        >
                          {togglingId === member.id ? (
                            <Loader2 className="w-5 h-5 animate-spin" />
                          ) : member.isActive ? (
                            <Eye className="w-5 h-5" />
                          ) : (
                            <EyeOff className="w-5 h-5" />
                          )}
                        </button>

                        <Link
                          to={`/team/admin/edit/${member.id}`}
                          className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                          title="Edit member"
                        >
                          <Edit className="w-5 h-5" />
                        </Link>

                        <button
                          onClick={() => handleDelete(member.id, member.name)}
                          disabled={deletingId === member.id}
                          className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                          title="Delete member"
                        >
                          {deletingId === member.id ? (
                            <Loader2 className="w-5 h-5 animate-spin" />
                          ) : (
                            <Trash2 className="w-5 h-5" />
                          )}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </>
  );
};

export default TeamAdminPage;
